import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { Box, Button, Chip, CircularProgress, Stack } from "@mui/material";

import ObjectAnalysisInfoForm from "../ObjectAnalysisInfoForm";
import dataController from "@/lib/dataController";
import { Close, Done } from "@mui/icons-material";

import OperationsContext, {
  OperationsContextType,
} from "@/components/OperationsContext/OperationsContext";
import { DCRecord } from "@/@types/lib/dataController";
import api from "@/lib/api";

type IQualityControlInfoProps = {
  recordId?: number;
  dc: dataController;
  isQcChecked: boolean;
  qcFunctions: DCRecord[];
};

type IStatMessage = {
  msg: ReactNode;
  pass: boolean;
};

function QualityControlInfo(props: IQualityControlInfoProps) {
  const { recordId, dc, isQcChecked, qcFunctions } = props;

  const { t } = useTranslation();
  const operationsContext = useContext(
    OperationsContext
  ) as OperationsContextType;

  // const theme = useTheme();
  // const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  const apiInstance = new api();

  const [qcStatRecords, setQcStatRecords] = useState<Record<
    string,
    DCRecord
  > | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const handleGoToObjectAnalysis = () => {
    window.open("/object-analysis?object_id=" + recordId, "_blank");
  };

  const handleFetchQCStatus = () => {
    setIsLoading(true);
    const path = `qc/objekti/${recordId}/function-results/?projekt_id=${operationsContext.selectedProject?.id}`;
    apiInstance
      .Call(path, "get")
      .then((resp) => {
        if (resp.success && resp.data) {
          setQcStatRecords(resp.data as Record<string, DCRecord>);
        } else {
          setQcStatRecords(null);
        }
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!isQcChecked && operationsContext.selectedProject && recordId) {
      handleFetchQCStatus();
    } else {
      setQcStatRecords(null);
    }
  }, [recordId, isQcChecked]);

  const qcStatsMessages: IStatMessage[] = useMemo(() => {
    if (isQcChecked) return [];

    if (!qcFunctions.length) {
      return [
        {
          msg: t("qc.messages.unknown_error"),
          pass: false,
        },
      ];
    }

    const errorMessages: IStatMessage[] = [];

    if (qcStatRecords) {
      Object.entries(qcStatRecords).forEach((entry) => {
        const [key, value] = entry;

        const currentFunction = qcFunctions.find(
          (f) => f.procedure_name === key
        );
        if (!currentFunction) {
          return; // how did u get here?
        }
        const currentFunctionTitle = currentFunction.title;

        if (value !== null) {
          errorMessages.push({
            msg: currentFunctionTitle,
            pass: true,
          });
        } else {
          errorMessages.push({
            msg: currentFunctionTitle,
            pass: false,
          });
        }
      });
    }
    return errorMessages;
  }, [qcFunctions, qcStatRecords, isQcChecked]);

  return (
    <Stack maxHeight={"100%"}>
      <ObjectAnalysisInfoForm
        isPregledan={true} // to make sure its readonly while recycling component
        setNapomenaValue={() => {}}
        dc={dc}
        recordId={recordId}
        form="infoForm"
        mode="view"
        simpleNapomena
        noPadding
      />
      <Button
        size="small"
        disabled={!recordId}
        sx={{ width: "max-content", mb: "4px" }}
        variant="contained"
        onClick={handleGoToObjectAnalysis}
      >
        {t("buttons.object_analysis")}
      </Button>
      {recordId ? (
        isLoading ? (
          <Box width={"100%"} display={"flex"} justifyContent={"center"}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack gap={"4px"} height={"100%"} overflow={"auto"}>
            {qcStatsMessages.map((qcStat) => {
              return (
                <Chip
                  sx={{ width: "max-content" }}
                  icon={qcStat.pass ? <Done /> : <Close />}
                  label={qcStat.msg}
                  color={qcStat.pass ? "success" : "error"}
                  variant="outlined"
                  size="small"
                />
              );
            })}
          </Stack>
        )
      ) : null}
    </Stack>
  );
}

export default QualityControlInfo;
